import React, { useRef, useEffect, useState, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import ReactHtmlParser from 'react-html-parser';
import "./orderItem.css";
import "./button.css";
import { withTranslation } from "react-i18next";
import { getProductVariation, cartLocal } from "../../actions/productActions";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import ComboProductList from "./ComboProduct/comboProductList";

const URL = process.env.REACT_APP_API_BASE_IMG;
// Product name clamped to 2 lines. When truncated, the full name shows in a popover:
// on hover/focus (desktop) or on tap (mobile, closes on outside tap or Escape).
const ProductName = ({ name }) => {
  const wrapRef = useRef(null);
  const textRef = useRef(null);
  const popRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, arrow: 18 });

  // Rendered in a portal with fixed positioning, clamped to the viewport so it never
  // overflows the screen edge (cards are narrow on mobile).
  useLayoutEffect(() => {
    if (!open || !textRef.current || !popRef.current) return;
    const margin = 12;
    const r = textRef.current.getBoundingClientRect();
    const pw = popRef.current.offsetWidth;
    const ph = popRef.current.offsetHeight;
    const vw = document.documentElement.clientWidth;
    const left = Math.max(margin, Math.min(r.left, vw - pw - margin));
    const below = r.bottom + 8;
    const top = below + ph > window.innerHeight - margin && r.top - ph - 8 > margin ? r.top - ph - 8 : below;
    setPos({ top, left, arrow: Math.max(12, Math.min(r.left + 16 - left, pw - 24)) });
  }, [open, name]);

  useEffect(() => {
    if (!open) return undefined;
    const hide = () => setOpen(false);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [open]);

  const isTruncated = () => {
    const el = textRef.current;
    return !!el && el.scrollHeight > el.clientHeight + 1;
  };

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (e.type === "keydown" ? e.key === "Escape" : wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("touchstart", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("touchstart", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  const show = () => { if (isTruncated()) setOpen(true); };

  return (
    <div
      ref={wrapRef}
      className="product-name-wrap"
      onMouseEnter={show}
      onMouseLeave={() => setOpen(false)}
    >
      <h3
        ref={textRef}
        className="product-name"
        tabIndex={0}
        onFocus={show}
        onBlur={() => setOpen(false)}
        onClick={() => { if (!open) show(); }}
      >
        {name}
      </h3>
      {open && ReactDOM.createPortal(
        <div
          ref={popRef}
          className="product-name-popover"
          role="tooltip"
          style={{ top: pos.top, left: pos.left, "--arrow-left": `${pos.arrow}px` }}
        >
          {name}
        </div>,
        document.body
      )}
    </div>
  );
};

const OrderItem = (props) => {
  const data = props.data;
  const imgRefs = useRef([]);
  imgRefs.current = [];

  const [busyId, setBusyId] = useState(null);

  // Cart source of truth: Redux payload when present, otherwise localStorage (page reload).
  const readCart = () => {
    if (Array.isArray(props.add_to_cart)) return props.add_to_cart;
    try {
      const stored = JSON.parse(localStorage.getItem("cart") || "[]");
      return Array.isArray(stored) ? stored : [];
    } catch (e) {
      return [];
    }
  };
  const cartItems = readCart();
  const findCartItem = (product) => cartItems.find((item) => item.product_id === product.id);

  // Max purchasable qty; only applies when the product reports tracked stock.
  const getMaxQty = (product) => {
    const stock = Number(product.stock);
    return Number(product.enable_stock) === 1 && Number.isFinite(stock) ? stock : Infinity;
  };

  const commitCart = (cart) => {
    localStorage.setItem("cart", JSON.stringify(cart));
    props.cartLocal(cart);
    if (props.updateCart) props.updateCart(cart);
  };

  // Change cart qty for a direct-add product by delta; qty <= 0 removes the item.
  const changeCartQty = (key, delta) => {
    const product = props.data.product[key];
    if (busyId === product.id) return;
    setBusyId(product.id);

    const currentCart = JSON.parse(localStorage.getItem("cart") || "[]");
    const idx = currentCart.findIndex((item) => item.product_id === product.id);

    if (idx !== -1) {
      const item = currentCart[idx];
      const newQty = Math.min(Number(item.quantity) + delta, getMaxQty(product));
      if (newQty <= 0) {
        currentCart.splice(idx, 1);
      } else {
        item.price = (parseFloat(item.price) / Number(item.quantity)) * newQty;
        item.quantity = newQty;
      }
    } else if (delta > 0 && getMaxQty(product) >= 1) {
      // Generate unique cartID
      const cartID = parseInt(localStorage.getItem("cartID") || "0", 10) + 1;
      localStorage.setItem("cartID", cartID);

      currentCart.push({
        ...product,
        id: cartID,
        product_id: product.id,
        product_name: product.name,
        product_price: parseFloat(product.price),
        quantity: 1,
        price: parseFloat(product.price),
        variation_id: (product.variation_id).toString(),
        variation_name: "",
        variation_quantity: "",
        variation_set_id: "",
        variation_price: [],
        printed: 0,
      });

      // Fly-to-cart animation
      if (window.flyToCart && imgRefs.current[key]) {
        window.flyToCart({ current: imgRefs.current[key] });
      }
    }

    commitCart(currentCart);
    // Brief lock so rapid clicks can't race the cart update.
    setTimeout(() => setBusyId(null), 150);
  };

  const renderDirectAction = (product, key) => {
    const cartItem = findCartItem(product);
    const busy = busyId === product.id;
    if (!cartItem) {
      return (
        <button
          onClick={() => changeCartQty(key, 1)}
          disabled={busy}
          className="btn primary btn-item"
          data-testid={`${product.id}_direct_add`}
        >
          Add
        </button>
      );
    }
    const qty = Number(cartItem.quantity);
    return (
      <div className="qty-stepper" data-testid={`${product.id}_stepper`}>
        <button
          type="button"
          className="qty-btn"
          aria-label="Decrease quantity"
          disabled={busy}
          onClick={() => changeCartQty(key, -1)}
        >
          −
        </button>
        <span className="qty-value">{qty}</span>
        <button
          type="button"
          className="qty-btn"
          aria-label="Increase quantity"
          disabled={busy || qty >= getMaxQty(product)}
          onClick={() => changeCartQty(key, 1)}
        >
          +
        </button>
      </div>
    );
  };

  // Product opens the modifier modal (not a direct add) → it can be customized
  const isCustomizable = (item) =>
    !(item.combo_products?.length > 0) &&
    !(item.product_modifier_count === 0 || item.product_modifier_count_delivery === 0);

  const renderProduct = () => {
    let html = [];

    if (props.data.product && Object.keys(props.data.product).length > 0) {

      let data = props.data.product;

      Object.keys(data).forEach(function (key) {
        const item = data[key];
        const isNotForSelling =
          Number(item.out_of_stock) === 1;
        html.push(
          <div key={key} className="col-xl-6 ">
            <div className="box">
              {/* --- */}
              {/* {isNotForSelling && (
                <div className="not-selling-badge">Not For Selling</div>
              )} */}
              <div
                className={`row ${isNotForSelling ? "no-click-all" : ""}`}
                style={{ cursor: isNotForSelling ? "not-allowed" : "default" }}
              >
                <div className="col-6 product-img-wrapper">
                  {isNotForSelling && (
                    <div className="not-selling-cross">Out of Stock</div>
                  )}
                  <img
                    ref={(el) => (imgRefs.current[Number(key)] = el)}
                    className={`item-img ${isNotForSelling ? "img-gray" : ""}`}
                    src={(data && data[key].image) || '/images/default_placeholder.png'}
                    onError={(e) => { e.target.onerror = null; e.target.src = '/images/default_placeholder.png'; }}
                    alt="product"
                  />
                </div>

                <div className="col-6 cnts">
                  <div className="head">
                    <ProductName name={data && data[key].name} />
                  </div>
                  <div className="dec">
                    {data[key].product_description != null
                      ? ReactHtmlParser(data[key].product_description)
                      : null}
                  </div>
                  <div className="action">
                    <div className="price-row">
                      <div className="price" style={{ marginLeft: "0px" }}>
                        From <span>$ {data && parseFloat(data[key].price).toFixed(2)}</span>
                      </div>
                      {isCustomizable(data[key]) && (
                        <div
                          className="customizable-label"
                          role="button"
                          tabIndex={0}
                          data-toggle={props.AddToCaetrestrict ? undefined : "modal"}
                          data-target="#cartModal"
                          onClick={() => !isNotForSelling && !props.AddToCaetrestrict && props.showChooseModal(data[key])}
                          onKeyDown={(e) => {
                            if ((e.key === "Enter" || e.key === " ") && !isNotForSelling && !props.AddToCaetrestrict) {
                              props.showChooseModal(data[key]);
                            }
                          }}
                        >
                          <span className="customizable-icon" aria-hidden="true">✦</span>
                          Customizable
                        </div>
                      )}
                    </div>
                    {(Number(data[key].out_of_stock) === 1) ? (
                      <button className="btn primary btn-item btn-gray no-click" disabled>
                        Add
                      </button>
                    ) : props.AddToCaetrestrict ? (
                      <button className="btn primary btn-disabled" disabled>
                        Add
                      </button>
                    ) : data[key].combo_products?.length > 0 ? (
                      <button
                        onClick={() => props.showChooseModal(data[key])}
                        data-toggle="modal"
                        data-target="#cartModal"
                        className="btn primary btn-item"
                        data-testid={`${data[key].id}_combo_modal`}
                      >
                        Add
                      </button>
                    ) : (
                      // No restriction → decide based on modifier counts
                      data[key].product_modifier_count === 0 || data[key].product_modifier_count_delivery === 0 ? (
                        renderDirectAction(data[key], key)
                      ) : (
                        <button
                          onClick={() => props.showChooseModal(data[key])}
                          data-toggle="modal"
                          data-target="#cartModal"
                          className="btn primary btn-item"
                          data-testid={`${data[key].id}_modal`}
                        >
                          Add
                        </button>
                      ))

                    }
                  </div>
                </div>
              </div>
              {/* --- */}
            </div>
          </div>
        );
      });
    }
    return html;
  };

  return <div className="row">{renderProduct()}</div>;
};

OrderItem.propTypes = {
  showChooseModal: PropTypes.func.isRequired,
  updateCart: PropTypes.func,
  cartLocal: PropTypes.func.isRequired,
};

export { OrderItem }


const mapStateToProps = (state) => ({
  variation: state.products.variation,
  add_to_cart: state.products.add_to_cart,
});
export default withTranslation()(
  connect(mapStateToProps, { getProductVariation, cartLocal })(OrderItem)
);
